import {
  InstallmentsEnum,
  ModifyPropertyItem,
  OrderDetail,
  OrderModify,
  OrderStatus,
  Period827Enum,
  RequestProperty
} from '@insurance/api/requests'
import {
  deviceSession,
  formatDateToISOFormat,
  replaceEmptyWithUndefined
} from '@shared/utils'
import { AnyObject } from 'antd/es/_util/type'
import { NamePath } from 'antd/es/form/interface'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'

import { getCustomerFields, getMixtContractorFields } from '../PersonsFields'
import { PropertyFormValues } from './type'

export const INSURANCE_FORM_DATA_KEY = '__property_insurance_form_data__'
export const MIN_AGE = 18
export const CATEGORIES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j']

export const savePropertyValuesToStorage = (
  values: PropertyFormValues,
  id: string | number = ''
) => {
  const { prevStepKey } = formProps()
  const stringifyValues = JSON.stringify({
    ...values,
    [prevStepKey.name]: undefined
  })

  sessionStorage.setItem(id + INSURANCE_FORM_DATA_KEY, stringifyValues)
}

export const getPropertyValuesFromStorage = (id: string | number = '') => {
  const values = sessionStorage.getItem(id + INSURANCE_FORM_DATA_KEY)

  if (!values) return

  const { validFromProps, contractorProps, effectiveDateProps } = formProps()

  const parsedValues = JSON.parse(values)

  const validFromDate = parsedValues?.[validFromProps.name]
    ? dayjs(parsedValues?.[validFromProps.name])
    : undefined
  const personDateOfBirth =
    parsedValues[contractorProps.name]?.person?.date_of_birth

  if (personDateOfBirth)
    parsedValues[contractorProps.name].person.date_of_birth =
      dayjs(personDateOfBirth)

  return {
    ...parsedValues,
    ...(validFromDate?.isValid() && { [validFromProps.name]: validFromDate }),
    [effectiveDateProps.name]: parsedValues[effectiveDateProps.name]
      ? dayjs(parsedValues[effectiveDateProps.name])
      : undefined
  }
}

export const removePropertyValuesFromStorage = (id: string | number = '') =>
  sessionStorage.removeItem(id + INSURANCE_FORM_DATA_KEY)

export const normalizeCommonData = (
  values: PropertyFormValues,
  _includeContractor = false,
  includeCustomer = false
): RequestProperty => {
  const session = deviceSession()

  const itemsToProcess =
    !values.items || values.items.length === 0
      ? [
          {
            risks: [],
            cost: '',
            amount: ''
          }
        ]
      : values.items

  return {
    valid_from: formatDateToISOFormat(values.valid_from)!,
    franchise: values.franchise,
    items:
      itemsToProcess.map((item: AnyObject) => {
        const normalizedItem = {
          name: item.name,
          description: item.description,
          location: item.location,
          street: item.street,
          number: item.number,
          ...(item.block && { block: item.block }),
          ...(item.apartment && { apartment: item.apartment }),
          ...(item.country && { country: item.country }),
          district: item.district,
          category: item.category?.toUpperCase?.(),
          risks: item?.risks?.map((risk: number) => ({ risk })),
          cost: String(item.cost || ''),
          amount: String(item.amount || ''),
          ...(item.coefficients &&
            Array.isArray(item.coefficients) &&
            !!item?.coefficients?.length && {
              coefficients: item?.coefficients
                .filter(
                  (coef) =>
                    coef.coefficient !== undefined && coef.coefficient !== null
                )
                .map((coef) => {
                  const coefficientValue =
                    coef?.coefficient_value !== undefined &&
                    coef?.coefficient_value !== null
                      ? String(coef?.coefficient_value)
                      : coef?.value !== undefined && coef?.value !== null
                      ? String(coef?.value)
                      : ''

                  return {
                    coefficient: coef?.coefficient,
                    value: coefficientValue
                  }
                })
            })
        }

        const category = item.category?.toLowerCase()

        if (CATEGORIES.includes(category))
          normalizedItem[category] =
            replaceEmptyWithUndefined(item[category]) || {}

        return normalizedItem
      }) || [],
    ...(values.currency && { currency: values.currency }),
    ...(values.period && {
      period: String(values.period) as Period827Enum
    }),
    ...(values.installments && {
      installments: Number(values.installments) as InstallmentsEnum
    }),
    ...(includeCustomer &&
      values.customer && {
        customer: getCustomerFields(values.customer)
      }),
    ...getMixtContractorFields(values),
    session: {
      device_id: session.deviceId,
      session_id: session.sessionId
    }
  } as RequestProperty
}

export const normalizeOrderData = (values: PropertyFormValues) => {
  const prevValues = normalizeCommonData(values, false, true)

  return prevValues
}

export const normalizeModifyOrderData = (
  values: PropertyFormValues,
  order?: OrderDetail
): OrderModify => {
  const data = normalizeOrderData(values)

  const items = data.items.map((item, idx) => {
    const orderItem = order?.property?.items?.[idx]

    const isEveryCoefficientSame =
      !item?.coefficients ||
      item?.coefficients?.every((coefficient) =>
        orderItem?.coefficients?.every(
          (orderCoefficient) =>
            Number(orderCoefficient?.value) === Number(coefficient?.value)
        )
      )

    if (
      Number(item.amount) === Number(orderItem?.amount) &&
      Number(item.cost) === Number(orderItem?.cost) &&
      isEveryCoefficientSame
    )
      return {}

    return {
      amount: item.amount,
      cost: item.cost,
      coefficients: item.coefficients?.map(({ value }) => ({ value }))
    }
  }) as ModifyPropertyItem[]

  return {
    property: {
      effective_date: formatDateToISOFormat(values.effective_date)!,
      refund_destination: values.refund_destination,
      customer: data.customer,
      beneficiary: data.beneficiary,
      insured_person: data.insured_person,
      insured_same_as_contractor: data.insured_same_as_contractor,
      items
    }
  }
}

export const formProps = (tFunction?: TFunction) => {
  const t = tFunction ? tFunction : () => ''

  return {
    contractorProps: {
      name: 'contractor',
      label: t('contractor')
    },
    customerProps: {
      name: 'customer',
      label: t('customer')
    },
    validFromProps: {
      name: 'valid_from',
      label: t('valid_from')
    },
    periodProps: {
      name: 'period',
      label: t('period')
    },
    installmentsProps: {
      name: 'installments',
      label: t('installments')
    },
    costProps: {
      name: 'cost',
      label: t('real_price')
    },
    insuredAmountProps: {
      name: 'amount',
      label: t('insured_amount')
    },
    itemsKey: {
      name: 'items'
    },
    ItemCategoryProps: {
      name: 'category',
      label: t('category')
    },
    ItemRisksProps: {
      name: 'risks',
      label: t('risks')
    },
    ItemDescriptionProps: {
      name: 'description',
      label: t('description')
    },
    ItemLocationProps: {
      name: 'location',
      label: t('location')
    },
    ItemStreetProps: {
      name: 'street',
      label: t('street')
    },
    ItemNumberProps: {
      name: 'number',
      label: t('number')
    },
    ItemBlockProps: {
      name: 'block',
      label: t('block')
    },
    ItemApartmentProps: {
      name: 'apartment',
      label: t('apartment')
    },
    ItemCountryProps: {
      name: 'country',
      label: t('country')
    },
    ItemDistrictProps: {
      name: 'district',
      label: t('district')
    },
    ItemCoefficientProps: {
      name: 'coefficient',
      label: t('coefficient')
    },
    ItemCoefficientValueProps: {
      name: 'coefficient_value',
      label: t('coefficient_value')
    },
    ItemCoefficientsProps: {
      name: 'coefficients',
      label: t('coefficients')
    },
    effectiveDateProps: {
      name: 'effective_date',
      label: t('effective_date')
    },
    franchiseProps: {
      name: 'franchise',
      label: t('franchise')
    },

    // hidden fields
    stepKey: {
      name: 'step'
    },
    prevStepKey: {
      name: 'prev_step'
    },
    priceKey: {
      name: 'price'
    },
    orderKey: {
      name: 'order_id'
    },
    setBeneficiaryKey: {
      name: 'set_beneficiary_data'
    },
    isOnlineKey: {
      name: 'is_online_order'
    }
  } satisfies Record<string, { name: NamePath; label?: React.ReactNode }>
}

export const orderParentOrChildId = (order?: OrderDetail) =>
  !!order?.status &&
  [OrderStatus.COMPLETED, OrderStatus.PARTIAL_PAID].includes(order.status)
